import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import {
    TipranksAnalystRatingByStockSort,
    TipranksTrendingStocksFilter,
    TipranksTrendingStocksTrendingType,
} from '../constants/lookups';
import { MiddlewareInjectToken } from '../constants/tokens';
import { Apis, MiddlewareConfig } from '../types';
import {
    AnalystPortfolioItemWithExtraInfo,
    AnalystProfileInfo,
    TiprankOverviewWithExtraInfo,
    TipranksNewsSentiment,
} from '../types/Tipranks.type';
import { divide, multiply } from '../utils';
import { chunk } from 'lodash';

@injectable()
export class TipranksApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
    ) {
        super(apis, config);
    }

    async getTipranksAnalystRatingsBySymbol(
        symbols: string[],
        count?: number,
        sort?: TipranksAnalystRatingByStockSort,
    ) {
        const { data } = await this.apis.insight.tiprank.tiprankLatestAnalystRatingsOnStock({
            num: count,
            sort,
            tickers: symbols,
        });
        return data;
    }

    async getTipranksAssetsOverview(symbols: string[]): Promise<TiprankOverviewWithExtraInfo[]> {
        const maxQuotes = 150; // API limitations
        const responses = await Promise.all(
            chunk(symbols, maxQuotes).map(async (symbols) => this.apis.insight.tiprank.tiprankAnalystConsensus({
                include_analysts: false,
                tickers: symbols,
            }))
        );
        const response = responses.flatMap((response) => response.data);

        return response.map((tipRank) => {
            const totalRatings = tipRank.buy + tipRank.hold + tipRank.sell;
            const buyPercent = multiply(100, totalRatings === 0 ? 0 : divide(tipRank.buy, totalRatings));
            const holdPercent = multiply(100, totalRatings === 0 ? 0 : divide(tipRank.hold, totalRatings));
            const sellPercent = multiply(100, totalRatings === 0 ? 0 : divide(tipRank.sell, totalRatings));

            return {
                ...tipRank,
                totalRatings,
                buyPercent,
                holdPercent,
                sellPercent,
            };
        });
    }

    async getTipranksAnalystProfile(id: string): Promise<AnalystProfileInfo> {
        const { data } = await this.apis.insight.tiprank.tiprankAnalystProfile({ id });
        return data;
    }

    async getTipranksBestPerformingAnalysts(number = 20) {
        const { data } = await this.apis.insight.tiprank.tiprankBestPerformingExperts({
            num: number,
        });
        return data;
    }

    async getTipranksAnalystPortfolio(expertUID: string, count = 100): Promise<AnalystPortfolioItemWithExtraInfo[]> {
        const response = await this.apis.insight.tiprank.tiprankPortfolios({
            expert_uid: expertUID,
            number: count,
        });

        return response.data.map((item) => {
            const quote = item.quote;
            return {
                ...item,
                profit: item.price_target === 0 ? 0 : divide(quote.last_px, item.price_target),
            };
        });
    }

    async getTipranksNewsSentiment(symbol: string): Promise<TipranksNewsSentiment> {
        const response = await this.apis.insight.tiprank.tiprankNewsSentiment({ ticker: symbol });
        return response.data.news_sentiment;
    }

    async getTopAnalystRecommendations(
        count?: number,
        period?: number,
        filter?: TipranksTrendingStocksFilter,
        trendingType?: TipranksTrendingStocksTrendingType,
    ) {
        const { data } = await this.apis.insight.tiprank.tiprankTrendingStocks({
            filter,
            num: count,
            period,
            trending_type: trendingType,
        });
        return data;
    }

    async getLivefeedOfAnalystsRatings(count: number, bookmarkId?: number) {
        const { data } = await this.apis.insight.tiprank.tiprankLiveFeed({
            num: count,
            bookmark_id: bookmarkId,
        });
        return data;
    }
}
